import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
    TablePagination,
    Button,
    Box,
    FormControl,
    InputAdornment,
    OutlinedInput,
    Typography,
} from "@mui/material";

//Custom Components
import Table from "../../components/Table";
import ActionsMenu from "../../components/ActionsMenu";
import StudentModal from "./components/StudentModal";
import ChangePasswordModal from "../../components/ChangePasswordModal";
import { StudentColumns } from './components/StudentColumns';
// Icons
import AddIcon from '@mui/icons-material/Add';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import api from "../../utils/api";

const WEB_APP_BASE_URL =
    (process.env.REACT_APP_API_BASE_URL || "").replace(/\/api\/?$/, "") || window.location.origin;


export default function Students() {
    const location = useLocation();

    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({ page: 1, perPage: 40, total: 0, totalPages: 0 });
    const [activeFilters, setActiveFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [row, setRow] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

    const fetchRows = async (filters = activeFilters, options = {}) => {
        const { resetPage = false, overridePagination = {} } = options;

        const currentPage = resetPage ? 1 : overridePagination.page ?? pagination.page;
        const currentPerPage = overridePagination.perPage ?? pagination.perPage;

        setLoading(true);

        try {
            const response = await api.get("admin/students", {
                params: {
                    filter: filters,
                    page: currentPage,
                    perPage: currentPerPage,
                },
            });

            setPagination({
                page: currentPage,
                perPage: currentPerPage,
                total: response.data.meta.pagination.total,
                totalPages: response.data.meta.pagination.totalPages,
            });

            setRows(response.data.data);
        } catch (error) {
            console.error("Fetch failed", error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleModerated = useCallback(async (row) => {
        try {
            await api.put(`admin/students/${row.id}`, {
                is_moderated: !row.is_moderated,
            });
            fetchRows(activeFilters, { overridePagination: { page: pagination.page } });
        } catch (error) {
            console.error("Failed to update moderated status", error);
            window.alert("Failed to update moderated status.");
        }
    }, [activeFilters, pagination.page]); // eslint-disable-line react-hooks/exhaustive-deps

    const columns = useMemo(
        () => StudentColumns(location.search, handleToggleModerated),
        [location.search, handleToggleModerated]
    );

    const handleDeleteRows = async (rows) => {
        try {
            // Delete multiple students
            for (const rowId of rows) {
                await api.delete(`admin/students/${rowId}`);
            }
            fetchRows(activeFilters);
        } catch (err) {
            console.log(err)
        }
    };

    const handleOpenManageRow = (row) => {
        setRow(row);
        setIsModalOpen(true);
    };

    const handleCloseManageRow = (refetch) => {
        setRow(null);
        setIsModalOpen(false);
        if (refetch) {
            fetchRows();
        }
    };

    const handleOpenPasswordModal = (row) => {
        setRow(row);
        setIsPasswordModalOpen(true);
    };

    const handleClosePasswordModal = (refetch) => {
        setRow(null);
        setIsPasswordModalOpen(false);
        if (refetch) {
            fetchRows();
        }
    };

    const handlePageChange = (newPage) => {
        fetchRows(activeFilters, { overridePagination: { page: newPage + 1 } });
    };

    const handleLoginAsUser = async (userId) => {
        try {
            const response = await api.post(`session/generate-token/${userId}`);
            const token = response?.data?.data?.token;
            if (!token) {
                window.alert("Could not generate login token.");
                return;
            }
            const url = `${WEB_APP_BASE_URL}/auth/impersonate?token=${encodeURIComponent(token)}`;
            window.open(url, "_blank", "noopener,noreferrer");
        } catch (error) {
            console.error("Failed to generate impersonation token", error);
            if (error?.response?.status === 403) {
                window.alert("Only Super Admin can use Login as user.");
                return;
            }
            window.alert("Failed to login as selected user.");
        }
    };

    useEffect(() => {
        //fetchRows();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {       
        const delayDebounce = setTimeout(() => {
            const newFilters = { name: search };
            setActiveFilters(newFilters);
            fetchRows(newFilters, { resetPage: true });
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Students
            </Typography>
            <Box>
                <Box sx={{ display: "flex", mb: 2, justifyContent: "space-between" }}>
                    <Box>
                        <FormControl sx={{ width: { xs: '100%', md: '45ch' } }} variant="outlined">
                            <OutlinedInput
                                size="small"
                                id="search"
                                placeholder="Search..."
                                sx={{ flexGrow: 1, background: "#fff" }}
                                onChange={(e) => setSearch(e.target.value)}
                                value={search}
                                startAdornment={
                                    <InputAdornment position="start" sx={{ color: 'text.primary' }}>
                                        <SearchRoundedIcon fontSize="small" />
                                    </InputAdornment>
                                }
                                inputProps={{
                                    'aria-label': 'search',
                                }}
                            />
                        </FormControl>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Button variant="contained" size="small" startIcon={<AddIcon color='success' />} onClick={() => handleOpenManageRow()}>
                            Create New
                        </Button>
                    </Box>
                </Box>

                <Table
                    loading={loading}
                    rows={rows}
                    columns={columns}
                    selactable={false}
                    draggable={false}
                    renderActions={(row) => (
                        <ActionsMenu
                            actions={[
                                { label: "Edit", onClick: () => handleOpenManageRow(row) },
                                { label: "Profile", onClick: () => window.open(`/students/${row.id}`, "_blank", "noopener,noreferrer") },
                                { label: "Login", onClick: () => handleLoginAsUser(row.id) },
                                { label: "Change Password", onClick: () => handleOpenPasswordModal(row) },
                                {
                                    label: "Delete",
                                    onClick: () => handleDeleteRows([row.id]),
                                    confirm: true,
                                    confirmText: `Are you sure you want to delete student "${row.first_name} ${row.last_name}"?`,
                                },
                            ]}
                        />
                    )} />

                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={pagination.page - 1}
                    rowsPerPage={pagination.perPage}
                    onPageChange={(event, newPage) => {
                        handlePageChange(newPage)
                    }}
                    rowsPerPageOptions={[]}
                />
            </Box>
            <StudentModal
                row={row}
                open={isModalOpen}
                onClose={handleCloseManageRow}
            />
            <ChangePasswordModal
                row={row}
                open={isPasswordModalOpen}
                onClose={handleClosePasswordModal}
                endpoint="admin/students"
            />
        </Box >
    );
}
